
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { CalendarIcon } from "lucide-react";
import { format, differenceInYears, differenceInMonths, differenceInDays } from "date-fns";
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useToast } from '@/components/ui/use-toast';

const AgeCalculator = () => {
  const { toast } = useToast();
  const [birthDate, setBirthDate] = useState<Date | undefined>(undefined);
  const [targetDate, setTargetDate] = useState<Date | undefined>(new Date());
  const [age, setAge] = useState<{
    years: number;
    months: number;
    days: number;
  } | null>(null);

  const calculateAge = () => {
    if (!birthDate || !targetDate) {
      toast({
        title: "Missing dates",
        description: "Please select both birth date and target date",
        variant: "destructive",
      });
      return;
    }

    if (birthDate > targetDate) {
      toast({
        title: "Invalid dates",
        description: "Birth date cannot be after target date",
        variant: "destructive",
      });
      return;
    }

    const years = differenceInYears(targetDate, birthDate);
    const months = differenceInMonths(targetDate, birthDate) % 12;
    
    // Clone the target date and subtract years and months to calculate remaining days
    const tempDate = new Date(targetDate);
    tempDate.setFullYear(tempDate.getFullYear() - years);
    tempDate.setMonth(tempDate.getMonth() - months);
    
    const days = differenceInDays(tempDate, new Date(birthDate.getFullYear(), birthDate.getMonth(), birthDate.getDate()));
    
    setAge({ years, months, days });
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="grid md:grid-cols-2 gap-8">
        {/* Input Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Enter Dates</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="birth-date" className="font-medium text-gray-700">Birth Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      id="birth-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {birthDate ? format(birthDate, "PPP") : "Select birth date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={birthDate}
                      onSelect={setBirthDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="target-date" className="font-medium text-gray-700">Target Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      id="target-date"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {targetDate ? format(targetDate, "PPP") : "Select target date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={targetDate}
                      onSelect={setTargetDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Button 
                className="w-full mt-4" 
                onClick={calculateAge}
              >
                Calculate Age
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Age Results</h2>
            
            {age ? (
              <>
                <div className="text-center mb-6">
                  <div className="text-4xl font-bold mb-2 leading-none">
                    {age.years}
                    <span className="text-2xl text-gray-500"> years</span>
                  </div>
                </div>
                
                <Table>
                  <TableBody>
                    <TableRow className="bg-[#E6F3FF]">
                      <TableCell className="font-medium text-gray-700">Years</TableCell>
                      <TableCell className="text-right font-bold">{age.years}</TableCell>
                    </TableRow>
                    <TableRow className="bg-white">
                      <TableCell className="font-medium text-gray-700">Months</TableCell>
                      <TableCell className="text-right font-bold">{age.months}</TableCell>
                    </TableRow>
                    <TableRow className="bg-[#E6F3FF]">
                      <TableCell className="font-medium text-gray-700">Days</TableCell>
                      <TableCell className="text-right font-bold">{age.days}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                <div className="mt-6 text-sm text-gray-600">
                  <h3 className="font-semibold mb-2">Total Age Breakdown:</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>{age.years} years, {age.months} months, and {age.days} days</li>
                    <li>or {age.years * 12 + age.months} months and {age.days} days</li>
                    <li>or approximately {Math.floor((age.years * 365.25) + (age.months * 30.44) + age.days)} days</li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                Select dates and click calculate to see your age results
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgeCalculator;
